import type {
  BadgeAppearance,
  BadgeEmphasis,
  BadgeProps,
  BadgeVariant,
  BadgeSize,
  BadgeStatusDotColor,
  IconBadgeProps,
  CountBadgeProps,
} from './Badge.types';

/**
 * Variant-specific styling configuration
 * Based on design system: section 5.5 Feedback Components (Badges)
 */
const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-contrast-700 text-neutral-200',
  primary: 'bg-sakura-shade text-primary-300',
  secondary: 'bg-secondary-900 text-secondary-200',
  // eslint-disable-next-line do-tokens/no-non-surface-contrast -- neutral badge indicator fill, parallel to the semantic-900 variant bgs (not a surface)
  contrast: 'bg-contrast-900 text-contrast-200',
  accent: 'bg-accent-800 text-accent-200',
  success: 'bg-success-900 text-success-400',
  warning: 'bg-warning-900 text-warning-400',
  error: 'bg-error-900 text-error-400',
  info: 'bg-info-900 text-info-400',
};

/**
 * Soft styling — an alpha wash of the tone's `-400` with a same-family label.
 *
 * The app's hand-rolled badges are almost all translucent tints; no solid
 * variant could express that, which is why they bypassed this component.
 *
 * These are the mobile values (`VARIANT_SOFT_CONTAINER` / `VARIANT_SOFT_LABEL`
 * in `packages/mobile/src/components/ui/Badge/Badge.styles.ts`), and they must
 * stay that way: the two platforms render the same rows, so a change here is a
 * change there. The neutral tones sit on the opaque field surface because
 * there is no hue to wash.
 */
const softVariantStyles: Record<BadgeVariant, string> = {
  default: 'bg-contrast-600 text-neutral-400',
  primary: 'bg-sakura-tint text-primary-300',
  secondary: 'bg-secondary-400/15 text-secondary-300',
  contrast: 'bg-contrast-600 text-neutral-300',
  accent: 'bg-accent-400/15 text-accent-300',
  success: 'bg-success-400/15 text-success-400',
  warning: 'bg-warning-400/15 text-warning-400',
  error: 'bg-error-400/15 text-error-400',
  info: 'bg-info-400/15 text-info-400',
};

/**
 * `emphasis="strong"` lifts the wash to 0.20. Only the tones mobile gives a
 * stronger wash are listed; the rest keep their soft fill.
 */
const softStrongVariantStyles: Partial<Record<BadgeVariant, string>> = {
  primary: 'bg-sakura-shade text-primary-300',
  success: 'bg-success-400/20 text-success-400',
  warning: 'bg-warning-400/20 text-warning-400',
  error: 'bg-error-400/20 text-error-400',
  info: 'bg-info-400/20 text-info-400',
};

/** `glow` — the tone's coloured halo. Only tones with a glow token have one. */
const softGlowStyles: Partial<Record<BadgeVariant, string>> = {
  primary: 'shadow-sakura-glow',
  info: 'shadow-info-glow',
};

/**
 * `outlined` — a hairline inside the edge. Alpha-white like every other stroke,
 * except on `primary`, where the accent stroke is what marks the state.
 *
 * An inset ring rather than a border, so an outlined badge is the same size as
 * its unoutlined neighbours and can toggle without shifting the row.
 */
function outlineClasses(variant: BadgeVariant): string {
  return variant === 'primary'
    ? 'ring-1 ring-inset ring-stroke-accent'
    : 'ring-1 ring-inset ring-stroke-subtle';
}

/**
 * Resolves the fill for a variant/appearance/emphasis triple.
 */
function resolveVariantClasses(
  variant: BadgeVariant,
  appearance: BadgeAppearance,
  emphasis: BadgeEmphasis
): string {
  if (appearance === 'solid') return variantStyles[variant];
  return (
    (emphasis === 'strong' ? softStrongVariantStyles[variant] : undefined) ??
    softVariantStyles[variant]
  );
}

/**
 * Size-specific styling configuration
 */
const sizeStyles: Record<BadgeSize, { container: string; icon: string }> = {
  sm: {
    container: 'text-label-xs px-1.5 py-0.5 gap-1',
    icon: 'h-3 w-3',
  },
  md: {
    container: 'text-label-xs px-2 py-0.5 gap-1.5',
    icon: 'h-3.5 w-3.5',
  },
  lg: {
    container: 'text-label-sm px-2.5 py-1 gap-1.5',
    icon: 'h-4 w-4',
  },
};

/**
 * Status dot color mapping
 */
const statusDotColors: Record<BadgeStatusDotColor, string> = {
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
  neutral: 'bg-neutral-500',
  // A ring, not a fill — see BadgeStatusDotColor.
  hollow: 'border border-stroke-strong',
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
  contrast: 'bg-contrast-500',
};

/**
 * Badge component for displaying status indicators, labels, and counts
 *
 * @example
 * ```tsx
 * // Default badge
 * <Badge>Default</Badge>
 *
 * // Primary badge
 * <Badge variant="primary">Active</Badge>
 *
 * // Badge with icon
 * <Badge variant="success" icon={<CheckIcon />}>Completed</Badge>
 *
 * // Badge with status dot
 * <Badge variant="default" statusDot="success">Online</Badge>
 *
 * // Pill-shaped badge
 * <Badge variant="primary" pill>New</Badge>
 *
 * // Soft wash, stroked, with the tone's halo
 * <Badge variant="primary" appearance="soft" outlined glow>Live</Badge>
 * ```
 */
export function Badge({
  variant = 'default',
  appearance = 'solid',
  emphasis = 'default',
  size = 'md',
  children,
  icon,
  iconRight,
  statusDot,
  statusDotPulse = false,
  caps = false,
  pill = false,
  outlined = false,
  glow = false,
  className = '',
  ...props
}: BadgeProps) {
  const isSoft = appearance === 'soft';
  const variantClasses = resolveVariantClasses(variant, appearance, emphasis);
  const sizeClasses = sizeStyles[size];
  // Soft chips round tighter and set a touch heavier than solid badges (mobile
  // `SOFT_ROUNDED_RADIUS` / `SOFT_LABEL_WEIGHT`).
  const roundedClasses = pill
    ? 'rounded-full'
    : isSoft
      ? 'rounded-sm'
      : 'rounded-md';
  const weightClasses = isSoft ? 'font-semibold' : 'font-medium';

  return (
    <span
      className={`
        inline-flex items-center ${weightClasses}
        ${caps ? 'uppercase tracking-wide' : ''}
        ${variantClasses}
        ${outlined ? outlineClasses(variant) : ''}
        ${glow && isSoft ? (softGlowStyles[variant] ?? '') : ''}
        ${sizeClasses.container}
        ${roundedClasses}
        ${className}
      `
        .replace(/\s+/g, ' ')
        .trim()}
      {...props}
    >
      {/* Status dot indicator */}
      {statusDot && (
        <span
          className={`flex-shrink-0 h-1.5 w-1.5 rounded-full ${statusDotColors[statusDot]} ${
            statusDotPulse ? 'animate-pulse' : ''
          }`.trim()}
          aria-hidden="true"
        />
      )}

      {/* Left icon */}
      {icon && (
        <span
          className={`flex-shrink-0 ${sizeClasses.icon}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}

      {/* Content */}
      <span>{children}</span>

      {/* Right icon */}
      {iconRight && (
        <span
          className={`flex-shrink-0 ${sizeClasses.icon}`}
          aria-hidden="true"
        >
          {iconRight}
        </span>
      )}
    </span>
  );
}

/**
 * IconBadge component for icon-only badges
 *
 * @example
 * ```tsx
 * <IconBadge
 *   variant="primary"
 *   icon={<StarIcon />}
 *   aria-label="Favorite"
 * />
 * ```
 */
export function IconBadge({
  variant = 'default',
  appearance = 'solid',
  emphasis = 'default',
  size = 'md',
  icon,
  'aria-label': ariaLabel,
  className = '',
  ...props
}: IconBadgeProps) {
  const variantClasses = resolveVariantClasses(variant, appearance, emphasis);
  const sizeClasses = sizeStyles[size];

  // Adjust padding for icon-only badges
  const iconPadding: Record<BadgeSize, string> = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={`
        inline-flex items-center justify-center font-medium rounded-full
        ${variantClasses}
        ${iconPadding[size]}
        ${className}
      `.trim()}
      {...props}
    >
      <span className={`flex-shrink-0 ${sizeClasses.icon}`} aria-hidden="true">
        {icon}
      </span>
    </span>
  );
}

/**
 * CountBadge component for displaying notification counts
 *
 * @example
 * ```tsx
 * // Basic count
 * <CountBadge count={5} />
 *
 * // With maximum (shows "99+")
 * <CountBadge count={150} max={99} />
 *
 * // Error variant for unread notifications
 * <CountBadge count={3} variant="error" />
 *
 * // Show zero (by default zero is hidden)
 * <CountBadge count={0} showZero />
 * ```
 */
export function CountBadge({
  count,
  max = 99,
  variant = 'error',
  appearance = 'solid',
  emphasis = 'default',
  size = 'sm',
  showZero = false,
  className = '',
  ...props
}: CountBadgeProps) {
  // Don't render if count is 0 and showZero is false
  if (count === 0 && !showZero) {
    return null;
  }

  const displayCount = count > max ? `${max}+` : String(count);
  const variantClasses = resolveVariantClasses(variant, appearance, emphasis);
  const sizeClasses = sizeStyles[size];

  // Minimum width to ensure circular shape for single digits
  const minWidthClasses: Record<BadgeSize, string> = {
    sm: 'min-w-[18px]',
    md: 'min-w-5',
    lg: 'min-w-6',
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center font-medium rounded-full
        ${variantClasses}
        ${sizeClasses.container}
        ${minWidthClasses[size]}
        ${className}
      `.trim()}
      {...props}
    >
      {displayCount}
    </span>
  );
}

export default Badge;
